import { Box, Typography, Alert, Stack } from "@mui/material";
import { useSettlementReportsQuery } from "Api/Queries/Settlements/useSettlementReportsQuery";
import { useGetSettlementPeriodsQuery } from "Api/Queries/Transactions/useGetSettlementPeriodsQuery";
import { useState, useMemo } from "react";
import { SettlementListItem } from "Components/Settlements/SettlementListItem";
import { SettlementFilterControls } from "Components/Settlements/SettlementFilterControls";
import { SettlementFilter, SettlementSortBy } from "Infrastructure/Api/Api";
import type { SettlementUserRole } from "Types/settlements";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.Settlements;

type SettlementPeriodInfo = {
  currentDate: string | null | undefined;
  previousDate: string | null | undefined;
};

type Props = {
  partyPublicID?: string;
  userRole: SettlementUserRole;
  isReadOnly?: boolean;
};

export const SettlementsList = ({
  partyPublicID,
  userRole,
  isReadOnly = false,
}: Props) => {
  const [filter, setFilter] = useState<SettlementFilter>(SettlementFilter.All);
  const [sortBy, setSortBy] = useState<SettlementSortBy>(
    SettlementSortBy.DateDesc,
  );

  const { t } = useResource();

  const {
    data: settlementsData,
    isLoading: settlementsLoading,
    error: settlementsError,
  } = useSettlementReportsQuery(partyPublicID, [filter], sortBy);

  const {
    data: settlementPeriods = [],
    isLoading: periodsLoading,
    error: periodsError,
  } = useGetSettlementPeriodsQuery();

  const settlementPeriodMap = useMemo(() => {
    const map = new Map<number, SettlementPeriodInfo>();
    settlementPeriods.forEach(
      ({ settlementPeriodID, recordSettlementDate }, index) => {
        const previousPeriod = settlementPeriods[index + 1];
        map.set(settlementPeriodID, {
          currentDate: recordSettlementDate,
          previousDate: previousPeriod?.recordSettlementDate,
        });
      },
    );
    return map;
  }, [settlementPeriods]);

  if (settlementsLoading || periodsLoading) {
    return <CenteredCircularProgress />;
  }

  if (settlementsError || periodsError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {t(COMPONENT_RESOURCES.LoadingError)}
      </Alert>
    );
  }

  const settlements = settlementsData?.settlements;

  if (!settlements) {
    return (
      <Typography color="text.secondary">
        {t(COMPONENT_RESOURCES.NoSettlementsFound)}
      </Typography>
    );
  }

  return (
    <Stack gap={4}>
      <Box
        display="flex"
        flexDirection={["column", "row"]}
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <Typography variant="h6">
          {t(COMPONENT_RESOURCES.SettlementCount, {
            count: settlements.length,
            total: settlementsData.summary.total,
          })}
        </Typography>
        <Box display="flex" alignItems="center">
          <SettlementFilterControls
            summary={settlementsData.summary}
            filter={filter}
            sortBy={sortBy}
            onFilterChange={setFilter}
            onSortChange={setSortBy}
          />
        </Box>
      </Box>
      {settlements.length === 0 ? (
        <Typography color="text.secondary">
          {t(COMPONENT_RESOURCES.NoSettlementsFound)}
        </Typography>
      ) : (
        <Stack gap={2}>
          {settlements.map((settlement) => {
            const previousSettlementDate = settlement.settlementPeriodID
              ? (settlementPeriodMap.get(settlement.settlementPeriodID)
                  ?.previousDate ?? null)
              : null;

            return (
              <SettlementListItem
                key={settlement.settlementID}
                settlement={settlement}
                previousSettlementDate={previousSettlementDate}
                partyPublicID={partyPublicID}
                userRole={userRole}
                isReadOnly={isReadOnly}
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
